import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Stack, Typography } from '@mui/material';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { RiArrowLeftLine, RiArrowRightLine } from 'react-icons/ri';
import BackdropDialog from 'common/components/BackdropDialog';
import SelfReturnDetails from './SelfReturnDetails';

import { OrderInfoDelivery, OrderDeliveryService, PurchaseTypes, OrderInfo } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { api } from 'common/frontend/api';
import errorHandler from 'common/services/errorHandler';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import { getUniqueCarrierOptions } from './utils';
import { BatchManager } from 'common/frontend/firebase/firestore';
import { Address } from 'common/modules/atoms/addresses';
import AddressField from './AddressField';
import DeliveryHeader from './DeliveryHeader';
import CarrierSelect from './CarrierSelect';
import { SELF_RETURN_CARRIER } from 'common/modules/delivery/constants';
import Banner from 'components/Banner';
import TrackingIdInput from './TrackingIdInput';
import { ClientDbBatch, DbUpdateObject } from 'common/db/api/types';

interface DeliveryDetailsForm {
	fromAddress: Address;
	fromCarrierId: string;
	fromTrackingId: string;
	toAddress: Address;
	toCarrierId: string;
	toTrackingId: string;
}

interface Props {
	orderDeliveryService: OrderDeliveryService;
	orderInfoDelivery: OrderInfoDelivery;
	open: boolean;
	onClose: () => void;
}

const DeliveryDetailsDialog = (props: Props) => {
	const { orderDeliveryService, orderInfoDelivery, open, onClose } = props;
	const { name, from, to } = orderDeliveryService;
	const { t, getTranslation } = useTranslation();

	const rentalInfo = useSelector(RentalSelectors.orderInfo)!;
	const shopCarriers = useSelector(ShopSelectors.activeShopCarriers);
	const {
		carriersDeleted,
		showWarningBanner,
		deliveryOptionAndCarriersDeleted,
		isDeliveryOptionDeleted,
		isDeliveryCarrierDeleted,
		isPickupCarrierDeleted,
	} = useSelector(RentalSelectors.hasDeliveryWarnings);

	const rentalEndDate = rentalInfo.returnTimeNext;

	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const { handleSubmit, formState, control } = useForm<DeliveryDetailsForm>({
		defaultValues: {
			fromAddress: from?.location?.address ?? { address: '', zipCode: '', state: '', details: '' },
			fromCarrierId: from?.carrier?.id ?? '',
			fromTrackingId: from?.trackingId ?? '',
			toAddress: to?.location?.address ?? { address: '', zipCode: '', state: '', details: '' },
			toCarrierId: to?.carrier?.id ?? '',
			toTrackingId: to?.trackingId ?? '',
		},
		mode: 'onChange',
	});

	const updateDeliveryService = async (
		formData: DeliveryDetailsForm,
		orderDeliveryId: string,
		batch: ClientDbBatch,
	): Promise<void> => {
		const {
			fromAddress,
			fromCarrierId,
			fromTrackingId,
			toAddress,
			toCarrierId,
			toTrackingId,
		} = formData;
		const toCarrierName = getUniqueCarrierOptions(shopCarriers, to?.carrier).find(
			(c) => c.id === toCarrierId,
		)?.name ?? { def: '' };
		const fromCarrierName = getUniqueCarrierOptions(shopCarriers, from?.carrier).find(
			(c) => c.id === fromCarrierId,
		)?.name ?? { def: '' };

		const updates: DbUpdateObject<OrderDeliveryService> = {};

		if (!!orderDeliveryService.to) {
			updates['to.location.address'] = toAddress;
			updates['to.carrier.id'] = toCarrierId;
			updates['to.carrier.name'] = toCarrierName;
			updates['to.trackingId'] = toTrackingId;
		}

		if (!!orderDeliveryService.from) {
			if (!orderDeliveryService.from?.disabled) {
				updates['from.location.address'] = fromAddress;
				updates['from.carrier.id'] = fromCarrierId;
				updates['from.carrier.name'] = fromCarrierName;
			}
			updates['from.trackingId'] = fromTrackingId;
		}

		return api(batch).orderDeliveries.doc(orderDeliveryId).update(updates);
	};

	const updateOrderDeliveryService = async (
		formData: DeliveryDetailsForm,
		orderId: string,
		batch: ClientDbBatch,
	): Promise<void> => {
		const { fromCarrierId, fromTrackingId, toCarrierId, toTrackingId } = formData;
		const updates: DbUpdateObject<OrderInfo> = {};

		if (!!orderInfoDelivery.to) {
			updates['services.delivery.to.carrierId'] = toCarrierId;
			updates['services.delivery.to.trackingId'] = toTrackingId ?? null;
		}

		if (!!orderInfoDelivery.from) {
			updates['services.delivery.from.carrierId'] = fromCarrierId;
			updates['services.delivery.from.trackingId'] = fromTrackingId;
		}

		return api(batch).orders.doc(orderId).update(updates);
	};

	const handleSubmitForm: SubmitHandler<DeliveryDetailsForm> = async (data) => {
		if (Object.keys(formState.errors).length > 0) {
			return;
		}
		setLoading(true);
		try {
			const batchManager = new BatchManager();
			updateDeliveryService(data, orderDeliveryService.id, batchManager.batch());
			updateOrderDeliveryService(data, orderDeliveryService.orderId, batchManager.batch());
			await batchManager.commit();
			onClose();
		} catch (e: any) {
			errorHandler.report(e);
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = (action: 'click_away' | 'cancel' | 'confirm' | 'left-action') => {
		if (action === 'confirm') {
			return;
		}
		onClose();
	};

	const renderDeliverySection = () => {
		if (!to?.timeslot || !orderDeliveryService?.to) return null;

		return (
			<Stack direction="row" spacing={2}>
				<Box height={27} display="flex" alignItems="center" justifyContent="center">
					<RiArrowRightLine size={16} />
				</Box>
				<Stack direction="column" spacing={4} flex={1}>
					<DeliveryHeader type="delivery" timeslot={orderDeliveryService.to?.timeslot} />

					<Controller
						control={control}
						name="toAddress"
						render={({ field: { ref, ...field } }) => <AddressField {...field} />}
					/>
					<Stack direction="column" spacing={2}>
						<Controller
							control={control}
							name="toCarrierId"
							render={({ field: { ref, ...field } }) => (
								<CarrierSelect {...field} oldCarrier={to.carrier} />
							)}
						/>
						<Controller
							control={control}
							name="toTrackingId"
							render={({ field: { ref, ...field } }) => <TrackingIdInput {...field} />}
						/>
					</Stack>
				</Stack>
			</Stack>
		);
	};

	const renderSelfReturnSection = () => {
		return (
			<Stack direction="row" spacing={2} sx={{ mt: 4 }}>
				<Box height={27} display="flex" alignItems="center" justifyContent="center">
					<RiArrowLeftLine size={16} />
				</Box>
				<Stack direction="column" spacing={4} flex={1}>
					<SelfReturnDetails
						date={rentalEndDate!}
						handlingTimeMinutesAfter={from!.handlingTimeMinutes}
						returnInstructions={orderDeliveryService.returnInstructions}
					/>
					<Controller
						control={control}
						name="fromTrackingId"
						render={({ field: { ref, ...field } }) => <TrackingIdInput {...field} />}
					/>
				</Stack>
			</Stack>
		);
	};

	const renderPickupSection = () => {
		if (rentalInfo.purchaseType === PurchaseTypes.sales) return null;
		if (from?.carrier?.id === SELF_RETURN_CARRIER.id) return renderSelfReturnSection();
		if (!from?.timeslot || from?.disabled || !orderDeliveryService?.from || !rentalEndDate)
			return null;

		return (
			<Stack direction="row" spacing={2} sx={{ mt: 4 }}>
				<Box height={27} display="flex" alignItems="center" justifyContent="center">
					<RiArrowLeftLine size={16} />
				</Box>
				<Stack direction="column" spacing={4} flex={1}>
					<DeliveryHeader type="pickup" timeslot={orderDeliveryService?.from?.timeslot} />

					<Controller
						control={control}
						name="fromAddress"
						render={({ field: { ref, ...field } }) => <AddressField {...field} />}
					/>
					<Controller
						control={control}
						name="fromCarrierId"
						render={({ field: { ref, ...field } }) => (
							<CarrierSelect {...field} oldCarrier={from.carrier} />
						)}
					/>
					<Controller
						control={control}
						name="fromTrackingId"
						render={({ field: { ref, ...field } }) => <TrackingIdInput {...field} />}
					/>
				</Stack>
			</Stack>
		);
	};

	const notAvailableInfo = {
		all_deleted: t(
			'delivery.deliveryOptionAndCarrierNotAvailable',
			'Delivery option and carrier is no longer available',
		),
		delivery_deleted: t(
			'delivery.deliveryOptionDeleted',
			'Delivery option is no longer available.',
		),
		carriers_deleted: t(
			'delivery.deliveryPickupCarriersDeleted',
			'Delivery and pickup carriers are no longer available.',
		),
		delivery_carrier_deleted: t(
			'delivery.deliveryCarrierDeleted',
			'Delivery carrier is no longer available.',
		),
		pickup_carrier_deleted: t(
			'delivery.pickupCarrierDeleted',
			'Pickup carrier is no longer available.',
		),
		no_warning: null,
	};

	const unavailableStatus = deliveryOptionAndCarriersDeleted
		? 'all_deleted'
		: isDeliveryOptionDeleted
		? 'delivery_deleted'
		: carriersDeleted
		? 'carriers_deleted'
		: isDeliveryCarrierDeleted
		? 'delivery_carrier_deleted'
		: isPickupCarrierDeleted
		? 'pickup_carrier_deleted'
		: 'no_warning';

	const renderDeliveryNotAvailable = () => (
		<Box my={2}>
			<Banner
				title={t('delivery.deliveryNotAvailable', 'Delivery is not available.')}
				description={notAvailableInfo[unavailableStatus]}
				variant={'warning'}
			/>
		</Box>
	);

	return (
		<BackdropDialog
			isOpen={open}
			onClose={handleClose}
			label={getTranslation(name)}
			body={
				<Box>
					{showWarningBanner && renderDeliveryNotAvailable()}
					{renderDeliverySection()}
					{renderPickupSection()}
					{error && (
						<Box mt={1}>
							<Typography variant="body2" color="error">
								{error}
							</Typography>
						</Box>
					)}
				</Box>
			}
			variant="primary"
			zIndex={1300}
			confirm={{
				label: t('common:actions.save'),
				callback: handleSubmit(handleSubmitForm),
				loading,
			}}
			cancel={{
				label: t('common:actions.cancel'),
			}}
		/>
	);
};

export default DeliveryDetailsDialog;
